import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";

import cls from "./GameEnterPage.module.scss";
import { accessGame } from "../../features/game/gameApi";
import {
  clearGameGuestToken,
  clearGamePlayer,
  setGameAccessToken,
  setGameEntry,
  setGamePlayerOptions,
} from "../../features/game/gameSession";

import MrM from "@/assets/UI/game/MrM.webp";
import MrsM from "@/assets/UI/game/MrsM.webp";
import MrD from "@/assets/UI/game/MrD.webp";
import MrsD from "@/assets/UI/game/MrsD.webp";

const floatSlow = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [0.5, 0.85, 0.5],
    scale: [1, 1.04, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [1, 0.85, 1],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

export default function GameEnterPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [joinName, setJoinName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(location.state?.message || "");

  const getErrorMessage = (error) =>
    error?.response?.data?.message ||
    error?.response?.data?.msg ||
    "Invalid join name or password.";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!joinName || !password) {
      setErr("Join name and password are required.");
      return;
    }

    try {
      setLoading(true);

      const data = await accessGame({ joinName, password });

      setGameEntry(data.game);
      setGameAccessToken(data.gameAccessToken);
      setGamePlayerOptions(data.existingPlayers || []);
      clearGameGuestToken();
      clearGamePlayer();

      navigate("/game/profile-setup");
    } catch (error) {
      setErr(getErrorMessage(error));
      setLoading(false);
    }
  };

  return (
    <main className={cls.page}>
      <div className={cls.pageInner}>
        <section className={cls.heroStage} aria-label="Guess My Desire">
          <div className={cls.mobileOnly} aria-hidden="true">
            <Motion.img
              className={`${cls.heroImg} ${cls.z0}`}
              src={MrM}
              alt=""
              variants={floatSlow}
              initial="hidden"
              animate="show"
              draggable="false"
            />
            <Motion.img
              className={`${cls.heroImg} ${cls.z1}`}
              src={MrsM}
              alt=""
              variants={floatSlowAlt}
              initial="hidden"
              animate="show"
              draggable="false"
            />
          </div>

          <div className={cls.desktopOnly} aria-hidden="true">
            <Motion.img
              className={`${cls.heroImg} ${cls.z0}`}
              src={MrD}
              alt=""
              variants={floatSlow}
              initial="hidden"
              animate="show"
              draggable="false"
            />
            <Motion.img
              className={`${cls.heroImg} ${cls.z1}`}
              src={MrsD}
              alt=""
              variants={floatSlowAlt}
              initial="hidden"
              animate="show"
              draggable="false"
            />
          </div>

          <div className={cls.formOverlay}>
            <div className={cls.formCard}>
              <div className={cls.formTop}>
                <div className={cls.formMeta}>
                  <div className={cls.formTitle}>Guess My Desire</div>

                  <div className={cls.formSub}>
                    The perfect way to break the ice… before the night starts to
                    heat up. A little mystery. A little tension. A lot of
                    curiosity. Read the room. Trust your instincts. Because when
                    the moment comes… you either do the dare or lose a layer.
                  </div>
                </div>
              </div>

              {err && <div className={cls.errorText}>{err}</div>}

              <form className={cls.form} onSubmit={submit}>
                <div className={cls.group}>
                  <label className={cls.label}>Game Join Name</label>
                  <input
                    className={cls.input}
                    placeholder="Enter join name"
                    value={joinName}
                    onChange={(e) => setJoinName(e.target.value)}
                    required
                  />
                </div>

                <div className={cls.group}>
                  <label className={cls.label}>Game Password</label>

                  <div className={cls.row}>
                    <input
                      className={cls.input}
                      type={showPwd ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />

                    <button
                      type="button"
                      className={cls.eyeBtn}
                      onClick={() => setShowPwd((v) => !v)}
                    >
                      {showPwd ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                <button className={cls.submit} disabled={loading}>
                  {loading ? "Entering..." : "Continue"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
